import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/auth';

const AdminBulletins = () => {
  const [bulletins, setBulletins] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0 });
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [selectedBulletin, setSelectedBulletin] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDecision, setFilterDecision] = useState('all');
  const [editAppreciation, setEditAppreciation] = useState('');
  const [decisionForm, setDecisionForm] = useState({
    decision: '',
    decision_comment: '',
    next_class_id: '',
  });
  const [genForm, setGenForm] = useState({
    class_id: '',
    period: 'trimestre_1',
    academic_year: '2026-2027',
  });

  // 📊 Vérification résultats
  const [showVerif, setShowVerif] = useState(false);
  const [verifClass, setVerifClass] = useState('');
  const [verifPeriod, setVerifPeriod] = useState('trimestre_1');
  const [verifAcademicYear, setVerifAcademicYear] = useState('2026-2027');
  const [verifResults, setVerifResults] = useState(null);
  const [verifLoading, setVerifLoading] = useState(false);
  const [selectedStudentDetail, setSelectedStudentDetail] = useState(null);

  const periods = [
    { value: 'trimestre_1', label: '1er Trimestre' },
    { value: 'trimestre_2', label: '2ème Trimestre' },
    { value: 'trimestre_3', label: '3ème Trimestre' },
    { value: 'annuel', label: 'Annuel' },
  ];

  const decisions = [
    { value: 'admis', label: '🟢 Admis(e) en classe supérieure' },
    { value: 'redouble', label: '🔴 Redouble' },
    { value: 'risque_redoublement', label: '🟠 Risque de redoublement' },
    { value: 'a_examiner', label: '⚠️ À examiner' },
    { value: 'en_attente', label: '⏳ En attente de décision' },
  ];

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [rcRes, statsRes, classRes] = await Promise.all([
        api.get('/report-cards', { headers }),
        api.get('/report-cards/stats', { headers }),
        api.get('/classes', { headers }),
      ]);

      setBulletins(Array.isArray(rcRes.data) ? rcRes.data : []);
      setStats(statsRes.data || { total: 0, published: 0, draft: 0 });
      setClasses(Array.isArray(classRes.data) ? classRes.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  // 📊 CHARGER LES RÉSULTATS DE LA CLASSE
  const loadClassResults = async () => {
    if (!verifClass) {
      alert('Sélectionnez une classe');
      return;
    }
    try {
      setVerifLoading(true);
      setSelectedStudentDetail(null);
      const token = localStorage.getItem('token');
      const res = await api.get(`/class-results/${verifClass}`, {
        params: { period: verifPeriod, academic_year: verifAcademicYear },
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerifResults(res.data);
    } catch (err) {
      console.error('Erreur:', err);
      alert('❌ ' + (err.response?.data?.error || err.message));
      setVerifResults(null);
    } finally {
      setVerifLoading(false);
    }
  };

  // ✅ GÉNÉRER LES BULLETINS DEPUIS LA VÉRIFICATION
  const generateFromVerif = async () => {
    if (!verifClass) {
      alert('Sélectionnez une classe');
      return;
    }
    if (!window.confirm(`Générer les bulletins pour cette classe (${verifResults?.students?.length || 0} élèves) ?`)) return;

    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      const res = await api.post('/report-cards/generate', {
        class_id: verifClass,
        period: verifPeriod,
        academic_year: verifAcademicYear,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`✅ ${res.data.message}`);
      setShowVerif(false);
      setVerifResults(null);
      fetchAll();
    } catch (err) {
      console.error('Erreur:', err);
      alert('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      const res = await api.post('/report-cards/generate', genForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`✅ ${res.data.message}`);
      setShowGenerateModal(false);
      fetchAll();
    } catch (err) {
      console.error('Erreur:', err);
      alert('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async (id) => {
    if (!window.confirm('Publier ce bulletin ?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.post(`/report-cards/${id}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAll();
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur');
    }
  };

  const handlePublishBatch = async () => {
    if (!genForm.class_id || !genForm.period) {
      alert('Sélectionnez une classe et une période d\'abord');
      return;
    }
    if (!window.confirm('Publier TOUS les bulletins en brouillon de cette classe/période ?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await api.post('/report-cards/publish-batch', genForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`✅ ${res.data.message}`);
      fetchAll();
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce bulletin ?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/report-cards/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAll();
    } catch (err) {
      console.error('Erreur:', err);
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const openDetail = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/report-cards/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBulletin(res.data);
      setEditAppreciation(res.data.appreciation || '');
      setShowDetailModal(true);
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur');
    }
  };

  const handleSaveAppreciation = async () => {
    try {
      const token = localStorage.getItem('token');
      await api.put(`/report-cards/${selectedBulletin.id}`, {
        appreciation: editAppreciation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Appréciation enregistrée');
      setShowDetailModal(false);
      fetchAll();
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur');
    }
  };

  const handlePrint = (id) => {
    window.open(`/admin/bulletins/${id}/print`, '_blank');
  };

  const openDecisionModal = (b) => {
    setSelectedBulletin(b);
    setDecisionForm({
      decision: b.decision || '',
      decision_comment: b.decision_comment || '',
      next_class_id: b.next_class_id || '',
    });
    setShowDecisionModal(true);
  };

  const suggestNextClass = (classId) => {
    const currentClass = classes.find((c) => String(c.id) === String(classId));
    if (!currentClass) return '';
    const order = ['6ème', '5ème', '4ème', '3ème', '2nde', '1ère', 'Terminale'];
    const idx = order.indexOf(currentClass.level);
    if (idx < 0 || idx >= order.length - 1) return '';
    const nextLevel = order[idx + 1];
    const nextClass = classes.find((c) => c.level === nextLevel);
    return nextClass ? nextClass.id : '';
  };

  const handleSaveDecision = async () => {
    if (!decisionForm.decision) {
      alert('Veuillez choisir une décision');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await api.put(`/report-cards/${selectedBulletin.id}/decision`, {
        decision: decisionForm.decision,
        decision_comment: decisionForm.decision_comment || null,
        next_class_id: decisionForm.next_class_id || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Décision enregistrée');
      setShowDecisionModal(false);
      fetchAll();
    } catch (err) {
      console.error('Erreur:', err);
      alert('❌ ' + (err.response?.data?.error || err.message));
    }
  };

  const getDecisionBadge = (decision) => {
    switch (decision) {
      case 'admis':
        return <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">🟢 Admis</span>;
      case 'redouble':
        return <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">🔴 Redouble</span>;
      case 'risque_redoublement':
        return <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">🟠 Risque</span>;
      case 'a_examiner':
        return <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">⚠️ À examiner</span>;
      case 'en_attente':
        return <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">⏳ En attente</span>;
      default:
        return <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">-</span>;
    }
  };

  const filteredBulletins = useMemo(() => {
    return bulletins.filter((b) => {
      if (searchTerm) {
        const t = searchTerm.toLowerCase();
        if (!(b.student_name || '').toLowerCase().includes(t)) return false;
      }
      if (filterClass !== 'all' && String(b.class_id) !== String(filterClass)) return false;
      if (filterPeriod !== 'all' && b.period !== filterPeriod) return false;
      if (filterStatus !== 'all' && b.status !== filterStatus) return false;
      if (filterDecision !== 'all') {
        if (filterDecision === 'none') {
          if (b.decision) return false;
        } else if (b.decision !== filterDecision) {
          return false;
        }
      }
      return true;
    });
  }, [bulletins, searchTerm, filterClass, filterPeriod, filterStatus, filterDecision]);

  const getAverageColor = (avg) => {
    if (avg >= 16) return 'text-green-600';
    if (avg >= 14) return 'text-blue-600';
    if (avg >= 12) return 'text-yellow-600';
    if (avg >= 10) return 'text-orange-600';
    return 'text-red-600';
  };

  const decisionStats = useMemo(() => {
    return {
      admis: bulletins.filter(b => b.decision === 'admis').length,
      redouble: bulletins.filter(b => b.decision === 'redouble').length,
      risque: bulletins.filter(b => b.decision === 'risque_redoublement').length,
      none: bulletins.filter(b => !b.decision).length,
    };
  }, [bulletins]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">📄 Gestion des bulletins</h1>
          <p className="text-gray-500">Vérifiez les résultats → Générez → Décidez → Publiez</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowVerif(!showVerif)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-medium"
          >
            {showVerif ? '✖️ Fermer la vérification' : '🔍 Vérifier les résultats'}
          </button>
          <button
            onClick={handlePublishBatch}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition font-medium"
          >
            📢 Publier par lot
          </button>
        </div>
      </div>

      {/* 📊 PANNEAU VÉRIFICATION */}
      {showVerif && (
        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-blue-500">
          <h2 className="text-xl font-bold text-[#2C3E50] mb-4">🔍 Vérification des résultats de la classe</h2>

          {/* Filtres */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium mb-1">Classe *</label>
              <select
                value={verifClass}
                onChange={(e) => setVerifClass(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">-- Choisir --</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Période *</label>
              <select
                value={verifPeriod}
                onChange={(e) => setVerifPeriod(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                {periods.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Année scolaire</label>
              <input
                type="text"
                value={verifAcademicYear}
                onChange={(e) => setVerifAcademicYear(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={loadClassResults}
                disabled={verifLoading || !verifClass}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {verifLoading ? '⏳ Chargement...' : '📊 Charger les résultats'}
              </button>
            </div>
          </div>

          {/* Résultats */}
          {verifResults && (
            <>
              <div className="bg-blue-50 rounded-lg p-3 mb-4 flex flex-wrap gap-4">
                <p className="text-sm">
                  <strong>Classe :</strong> {classes.find(c => String(c.id) === String(verifClass))?.name || '-'}
                </p>
                <p className="text-sm">
                  <strong>Période :</strong> {periods.find(p => p.value === verifPeriod)?.label}
                </p>
                <p className="text-sm">
                  <strong>Élèves :</strong> {verifResults.total_students}
                </p>
              </div>

              {verifResults.students.length === 0 ? (
                <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4 mb-4">
                  <p className="text-sm text-yellow-700">
                    ⚠️ <strong>Aucune note validée</strong> pour cette classe sur cette période.
                    Il faut que les professeurs saisissent les notes ET que l'admin les valide.
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg mb-4 max-h-96">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Rang</th>
                          <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Élève</th>
                          {verifResults.subjects.map((s) => (
                            <th key={s.id} className="text-center py-2 px-2 text-xs font-semibold text-gray-600">
                              {s.name}
                            </th>
                          ))}
                          <th className="text-center py-2 px-2 text-xs font-semibold text-gray-600">Moy.</th>
                          <th className="text-center py-2 px-2 text-xs font-semibold text-gray-600">Abs.</th>
                          <th className="text-center py-2 px-2 text-xs font-semibold text-gray-600">Détail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {verifResults.students.map((s) => (
                          <tr key={s.student_id} className="border-t hover:bg-gray-50">
                            <td className="py-2 px-3">
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                s.rank === 1 ? 'bg-yellow-400 text-white' :
                                s.rank === 2 ? 'bg-gray-300 text-white' :
                                s.rank === 3 ? 'bg-orange-400 text-white' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {s.rank}
                              </span>
                            </td>
                            <td className="py-2 px-3 font-medium">{s.student_name}</td>
                            {verifResults.subjects.map((subj) => {
                              const studentSubj = s.subjects.find(x => x.subject_id === subj.id);
                              return (
                                <td key={subj.id} className={`py-2 px-2 text-center font-semibold ${studentSubj ? getAverageColor(studentSubj.average) : 'text-gray-300'}`}>
                                  {studentSubj ? studentSubj.average.toFixed(2) : '-'}
                                </td>
                              );
                            })}
                            <td className={`py-2 px-2 text-center font-bold ${getAverageColor(s.general_average)}`}>
                              {s.general_average.toFixed(2)}
                            </td>
                            <td className="py-2 px-2 text-center text-xs">
                              <span className="text-red-600 font-medium">{s.absences_count}</span>
                              {s.lates_count > 0 && <span className="text-orange-600 ml-1">/{s.lates_count}</span>}
                            </td>
                            <td className="py-2 px-2 text-center">
                              <button
                                onClick={() => setSelectedStudentDetail(s)}
                                className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100"
                              >
                                👁️
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setVerifResults(null)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={generateFromVerif}
                      disabled={generating}
                      className="px-6 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] font-medium disabled:opacity-50"
                    >
                      {generating ? '⏳ Génération...' : '✅ Générer les bulletins pour cette classe'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          ℹ️ <strong>Conseil :</strong> Cliquez sur <strong>🔍 Vérifier les résultats</strong> pour voir les notes et absences
          avant de générer les bulletins.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
          <p className="text-xs text-gray-500 mb-1">📋 Total</p>
          <p className="text-xl font-bold text-[#2C3E50]">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
          <p className="text-xs text-gray-500 mb-1">🟢 Publiés</p>
          <p className="text-xl font-bold text-green-600">{stats.published}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-yellow-500">
          <p className="text-xs text-gray-500 mb-1">🟡 Brouillons</p>
          <p className="text-xl font-bold text-yellow-600">{stats.draft}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-600">
          <p className="text-xs text-gray-500 mb-1">🟢 Admis</p>
          <p className="text-xl font-bold text-green-600">{decisionStats.admis}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
          <p className="text-xs text-gray-500 mb-1">🔴 Redoublent</p>
          <p className="text-xl font-bold text-red-600">{decisionStats.redouble}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-gray-400">
          <p className="text-xs text-gray-500 mb-1">❓ Sans décision</p>
          <p className="text-xl font-bold text-gray-600">{decisionStats.none}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input
            type="text"
            placeholder="🔍 Rechercher (élève)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          />
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Toutes les classes</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={filterPeriod}
            onChange={(e) => setFilterPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Toutes les périodes</option>
            {periods.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Tous les statuts</option>
            <option value="draft">🟡 Brouillon</option>
            <option value="published">🟢 Publié</option>
          </select>
          <select
            value={filterDecision}
            onChange={(e) => setFilterDecision(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Toutes les décisions</option>
            <option value="none">❓ Sans décision</option>
            {decisions.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
        <p className="text-xs text-gray-500 mt-2">{filteredBulletins.length} bulletin(s)</p>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      ) : filteredBulletins.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-lg font-medium text-gray-700 mb-2">Aucun bulletin généré</p>
          <p className="text-sm text-gray-500 mb-4">
            Cliquez sur <strong>🔍 Vérifier les résultats</strong> en haut pour commencer.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Élève</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Classe</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Période</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Moyenne</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Rang</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Décision</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Statut</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBulletins.map((b) => (
                  <tr key={b.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{b.student_name}</td>
                    <td className="py-3 px-4 text-sm">{b.class_name}</td>
                    <td className="py-3 px-4 text-sm">{b.period_label}</td>
                    <td className={`py-3 px-4 font-bold ${getAverageColor(b.general_average)}`}>
                      {b.general_average?.toFixed(2)}/20
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {b.rank ? `${b.rank}/${b.total_students}` : '-'}
                    </td>
                    <td className="py-3 px-4">{getDecisionBadge(b.decision)}</td>
                    <td className="py-3 px-4">
                      {b.status === 'published' ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">🟢 Publié</span>
                      ) : (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">🟡 Brouillon</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1 flex-wrap">
                        <button
                          onClick={() => openDetail(b.id)}
                          className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100"
                          title="Voir"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => handlePrint(b.id)}
                          className="text-xs bg-gray-50 text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                          title="Imprimer"
                        >
                          🖨️
                        </button>
                        <button
                          onClick={() => openDecisionModal(b)}
                          className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded hover:bg-purple-100"
                          title="Définir la décision"
                        >
                          🎓
                        </button>
                        {b.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handlePublish(b.id)}
                              className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded hover:bg-green-100"
                              title="Publier"
                            >
                              📢
                            </button>
                            <button
                              onClick={() => handleDelete(b.id)}
                              className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded hover:bg-red-100"
                              title="Supprimer"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Détail Élève (Vérification) */}
      {selectedStudentDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl my-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-[#2C3E50]">👨‍🎓 {selectedStudentDetail.student_name}</h2>
                <p className="text-sm text-gray-500">
                  Matricule : {selectedStudentDetail.matricule || '-'} • Rang : {selectedStudentDetail.rank}/{selectedStudentDetail.total_students}
                </p>
              </div>
              <button
                onClick={() => setSelectedStudentDetail(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Moyenne</p>
                <p className={`text-2xl font-bold ${getAverageColor(selectedStudentDetail.general_average)}`}>
                  {selectedStudentDetail.general_average.toFixed(2)}/20
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Absences</p>
                <p className="text-2xl font-bold text-red-600">{selectedStudentDetail.absences_count}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Retards</p>
                <p className="text-2xl font-bold text-orange-600">{selectedStudentDetail.lates_count}</p>
              </div>
            </div>

            <h3 className="font-bold text-[#2C3E50] mb-2">📚 Détail par matière</h3>
            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              {selectedStudentDetail.subjects.length === 0 ? (
                <p className="p-4 text-center text-gray-500">Aucune note</p>
              ) : (
                selectedStudentDetail.subjects.map((subj, idx) => (
                  <div key={idx} className="border-b last:border-0 p-4">
                    <div className="flex justify-between items-center mb-2">
                      <p className="font-bold text-[#2C3E50]">📚 {subj.subject_name}</p>
                      <span className={`text-lg font-bold ${getAverageColor(subj.average)}`}>
                        {subj.average}/20 <span className="text-xs text-gray-500">(Coef {subj.coefficient})</span>
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      {subj.grades.map((g, gi) => (
                        <div key={gi} className="flex justify-between text-gray-600 bg-gray-50 rounded px-2 py-1">
                          <span>{g.title} <span className="text-xs text-gray-400">({g.type})</span></span>
                          <span className="font-medium">
                            {g.score}/{g.max_score} <span className="text-xs text-gray-400">(Coef {g.coefficient})</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Détail Bulletin */}
      {showDetailModal && selectedBulletin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl my-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-[#2C3E50]">📄 Bulletin</h2>
                <p className="text-sm text-gray-500">
                  {selectedBulletin.student?.user?.name} — {selectedBulletin.class?.name}
                </p>
                <p className="text-xs text-gray-500">
                  {selectedBulletin.period_label} — {selectedBulletin.academic_year}
                </p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Moyenne</p>
                <p className={`text-2xl font-bold ${getAverageColor(selectedBulletin.general_average)}`}>
                  {selectedBulletin.general_average?.toFixed(2)}/20
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Rang</p>
                <p className="text-2xl font-bold text-green-600">
                  {selectedBulletin.rank}/{selectedBulletin.total_students}
                </p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Statut</p>
                <p className="text-sm font-bold">
                  {selectedBulletin.status === 'published' ? '🟢 Publié' : '🟡 Brouillon'}
                </p>
              </div>
            </div>

            {selectedBulletin.decision && (
              <div className="bg-orange-50 border-l-4 border-[#E67E22] rounded-lg p-3 mb-4">
                <p className="text-xs font-bold text-[#E67E22] mb-1">🎓 DÉCISION SCOLAIRE</p>
                <p className="text-sm font-bold">{selectedBulletin.decision_label || selectedBulletin.decision}</p>
                {selectedBulletin.decision_comment && (
                  <p className="text-xs text-gray-600 italic mt-1">"{selectedBulletin.decision_comment}"</p>
                )}
              </div>
            )}

            <h3 className="font-bold text-[#2C3E50] mb-2">📚 Détail par matière</h3>
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg mb-4">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Matière</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Coef</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Moyenne</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedBulletin.data?.subjects || []).map((s, i) => (
                    <tr key={i} className="border-t">
                      <td className="py-2 px-3">{s.subject_name}</td>
                      <td className="py-2 px-3 text-sm">{s.coefficient}</td>
                      <td className={`py-2 px-3 font-bold ${getAverageColor(s.average)}`}>
                        {s.average}/20
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Appréciation générale</label>
              <textarea
                value={editAppreciation}
                onChange={(e) => setEditAppreciation(e.target.value)}
                rows="3"
                placeholder="Ex: Bon trimestre. Élève sérieux."
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div className="flex justify-end space-x-3 flex-wrap gap-2">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Fermer
              </button>
              <button
                onClick={() => handlePrint(selectedBulletin.id)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                🖨️ Imprimer
              </button>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  openDecisionModal(selectedBulletin);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                🎓 Décision
              </button>
              <button
                onClick={handleSaveAppreciation}
                className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400]"
              >
                💾 Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Décision */}
      {showDecisionModal && selectedBulletin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg my-8">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-[#2C3E50]">🎓 Décision scolaire</h2>
                <p className="text-sm text-gray-500">{selectedBulletin.student_name} — {selectedBulletin.class_name}</p>
                <p className="text-xs text-gray-500">
                  Moyenne : {selectedBulletin.general_average?.toFixed(2)}/20
                  {selectedBulletin.rank && ` • Rang : ${selectedBulletin.rank}/${selectedBulletin.total_students}`}
                </p>
              </div>
              <button
                onClick={() => setShowDecisionModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Décision *</label>
                <div className="space-y-2">
                  {decisions.map((d) => (
                    <label
                      key={d.value}
                      className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition ${
                        decisionForm.decision === d.value
                          ? 'border-[#E67E22] bg-orange-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="decision"
                        value={d.value}
                        checked={decisionForm.decision === d.value}
                        onChange={(e) => setDecisionForm({ ...decisionForm, decision: e.target.value })}
                      />
                      <span className="text-sm font-medium">{d.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {decisionForm.decision === 'admis' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Classe suivante</label>
                  <select
                    value={decisionForm.next_class_id}
                    onChange={(e) => setDecisionForm({ ...decisionForm, next_class_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">-- Choisir --</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const suggested = suggestNextClass(selectedBulletin.class_id);
                      setDecisionForm({ ...decisionForm, next_class_id: suggested });
                    }}
                    className="mt-2 text-xs text-blue-600 hover:underline"
                  >
                    💡 Suggérer automatiquement
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Motif / Observation</label>
                <textarea
                  value={decisionForm.decision_comment}
                  onChange={(e) => setDecisionForm({ ...decisionForm, decision_comment: e.target.value })}
                  rows="3"
                  placeholder="Ex: Bon travail. Élève sérieux et régulier."
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowDecisionModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveDecision}
                className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400]"
              >
                💾 Enregistrer la décision
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBulletins;